import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Leaf, Sun, Snowflake, Flower } from 'lucide-react';

const SeasonalServices = () => {
  const currentMonth = new Date().getMonth();
  
  // Determine current season (0-11 months)
  const getCurrentSeason = () => {
    if (currentMonth >= 2 && currentMonth <= 4) return 'spring';
    if (currentMonth >= 5 && currentMonth <= 7) return 'summer';
    if (currentMonth >= 8 && currentMonth <= 10) return 'fall';
    return 'winter';
  };

  const seasonalContent = {
    spring: {
      title: "Spring Cleaning Special - Ventura County",
      subtitle: "Refresh Your Property After Winter",
      description: "Spring in Ventura County brings pollen, dust, and the need for deep exterior cleaning. Our comprehensive spring cleaning package prepares your property for the beautiful California weather ahead.",
      icon: <Flower className="h-12 w-12 text-green-600" />,
      services: [
        "Deep pressure washing to remove winter buildup",
        "Window cleaning for crystal clear views",
        "Gutter cleaning and debris removal",
        "Solar panel cleaning for peak efficiency"
      ],
      cta: "Book Your Spring Cleaning Special",
      offer: "15% OFF comprehensive spring cleaning packages"
    },
    summer: {
      title: "Summer Maintenance - Beat the Heat",
      subtitle: "Keep Your Property Cool and Clean",
      description: "Ventura County's hot summers require specialized cleaning to combat dust, UV damage, and increased outdoor living. Maintain your property's beauty through the sunny season.",
      icon: <Sun className="h-12 w-12 text-yellow-600" />,
      services: [
        "Heat-resistant exterior cleaning",
        "Patio and deck deep cleaning",
        "Shade structure maintenance",
        "Pool area pressure washing"
      ],
      cta: "Schedule Summer Maintenance",
      offer: "Same-day service available during summer months"
    },
    fall: {
      title: "Fall Preparation - Ventura County",
      subtitle: "Prepare for Winter Weather",
      description: "As fall arrives in Ventura County, prepare your property for the rainy season. Our fall cleaning services, including emergency pressure washing after early storms, ensure your home is ready for whatever weather comes next.",
      icon: <Leaf className="h-12 w-12 text-orange-600" />,
      services: [
        "Gutter cleaning before rains arrive",
        "Exterior washing before winter",
        "Roof and siding maintenance",
        "Storm drain clearing"
      ],
      cta: "Get Fall Ready Today",
      offer: "Pre-winter maintenance packages available"
    },
    winter: {
      title: "Winter Touch-Ups - Mild Ventura Weather",
      subtitle: "Year-Round Cleaning in Perfect Weather",
      description: "Ventura County's mild winters are perfect for exterior maintenance. Take advantage of cooler temperatures and lower demand for premium cleaning services.",
      icon: <Snowflake className="h-12 w-12 text-blue-600" />,
      services: [
        "Emergency pressure washing after storms",
        "Post-storm cleanup services",
        "Winter window cleaning",
        "Holiday preparation cleaning",
        "New Year property refresh"
      ],
      cta: "Call for Discount",
      offer: "10% off all services booked this winter — call today to claim your discount!"
    }
  };

  const currentSeason = getCurrentSeason();
  const content = seasonalContent[currentSeason];

  const seasonalSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": content.title,
    "description": content.description,
    "provider": {
      "@type": "LocalBusiness",
      "name": "Ventura Power Washing",
      "telephone": "(805) 464-7166"
    },
    "areaServed": {
      "@type": "State",
      "name": "California"
    },
    "offers": {
      "@type": "Offer",
      "description": content.offer,
      "validFrom": new Date().toISOString(),
      "priceSpecification": {
        "@type": "PriceSpecification",
        "priceCurrency": "USD"
      }
    }
  };

  return (
    <section className="section-padding bg-gradient-to-br from-blue-50 to-green-50">
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(seasonalSchema)}
        </script>
      </Helmet>
      
      <div className="container mx-auto">
        <Card className="max-w-4xl mx-auto card-shadow bg-white">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <div className="flex justify-center mb-4">
                {content.icon}
              </div>
              <h2 className="text-3xl font-bold mb-2">{content.title}</h2>
              <h3 className="text-xl text-gray-600 mb-4">{content.subtitle}</h3>
              <p className="text-lg text-gray-700 max-w-2xl mx-auto">
                {content.description}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
              <div>
                <h4 className="text-xl font-semibold mb-4">
                  {getCurrentSeason().charAt(0).toUpperCase() + getCurrentSeason().slice(1)} Services Include:
                </h4>
                <ul className="space-y-2">
                  {content.services.map((service, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-600 mr-2">✓</span>
                      <span className="text-gray-700">{service}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-yellow-50 p-6 rounded-lg border border-yellow-200">
                <h4 className="text-lg font-semibold text-yellow-800 mb-2">
                  Limited Time Offer
                </h4>
                <p className="text-yellow-700 mb-4">{content.offer}</p>
                <p className="text-sm text-yellow-600">
                  Available for Ventura County residents. Call today!
                </p>
              </div>
            </div>

            <div className="text-center">
              <Button 
                className="bg-ventura-blue text-white hover:bg-ventura-darkBlue text-lg px-8 py-4 mr-4"
                onClick={() => window.location.href = "tel:+18054647166"}
              >
                {content.cta}
              </Button>
              <Button 
                variant="outline"
                className="text-ventura-blue border-ventura-blue hover:bg-ventura-blue hover:text-white text-lg px-8 py-4"
                onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Get Free Estimate
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default SeasonalServices;